import React, { useEffect, useRef, useState } from 'react';
import { FaRegBookmark, FaRegSmile } from 'react-icons/fa';

const EMOJIS = [
  '😊',
  '😉',
  '😘',
  '😍',
  '🥰',
  '😏',
  '😈',
  '🔥',
  '❤️',
  '💕',
  '💋',
  '🌹',
  '✨',
  '🙈',
  '😇',
  '😋',
  '🤭',
  '😌',
  '👀',
  '💌',
];

const ChatInput = ({
  textareaRef,
  newMessage,
  onChangeMessage,
  onSendMessage,
  isSending,
  onOpenSavedMessagesModal,
  onOpenMediaModal,
  mediaToSend,
  onRemoveMedia,
  mediaPrice,
  onMediaPriceChange,
}) => {
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const emojiPickerRef = useRef(null);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSendMessage();
    }
  };

  const isSendDisabled =
    isSending || (newMessage.trim() === '' && mediaToSend.length === 0);

  useEffect(() => {
    if (!isEmojiPickerOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setIsEmojiPickerOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsEmojiPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isEmojiPickerOpen]);

  const insertEmoji = (emoji) => {
    if (isSending) {
      return;
    }

    const textarea = textareaRef.current;
    const selectionStart = textarea?.selectionStart ?? newMessage.length;
    const selectionEnd = textarea?.selectionEnd ?? newMessage.length;
    const nextMessage = `${newMessage.slice(0, selectionStart)}${emoji}${newMessage.slice(selectionEnd)}`;
    const nextCursorPosition = selectionStart + emoji.length;

    onChangeMessage(nextMessage);
    setIsEmojiPickerOpen(false);

    window.requestAnimationFrame(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }
    });
  };

  return (
    <div className="chat-input-section">
      {mediaToSend.length > 0 && (
        <div className="media-preview-container">
          {mediaToSend.map((media) => (
            <div key={media._id} className="media-preview-item">
              <img
                src={media.src.small}
                alt="Prévisualisation média"
                className="media-preview-thumbnail"
              />
              <button
                onClick={() => onRemoveMedia(media._id)}
                className="remove-media-button"
                type="button"
                aria-label="Retirer ce média"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="chat-input-bar">
        <button
          className="saved-messages-button"
          onClick={onOpenSavedMessagesModal}
          type="button"
          aria-label="Ouvrir les messages enregistrés"
          title="Messages enregistrés"
        >
          <FaRegBookmark />
        </button>

        <button
          className="add-media-button"
          onClick={onOpenMediaModal}
          type="button"
          aria-label="Ajouter un média"
        >
          +
        </button>

        {mediaToSend.length > 0 && (
          <div className="price-input-wrapper">
            <input
              type="text"
              className="price-input-chat"
              placeholder="Prix"
              value={mediaPrice}
              onChange={(e) => onMediaPriceChange(e.target.value)}
              inputMode="numeric"
            />
            <span>€</span>
          </div>
        )}

        <div className="emoji-picker-wrapper" ref={emojiPickerRef}>
          <button
            className="emoji-picker-toggle"
            onClick={() => setIsEmojiPickerOpen((isOpen) => !isOpen)}
            type="button"
            aria-label="Ouvrir les emojis"
            aria-expanded={isEmojiPickerOpen}
            disabled={isSending}
            title="Emojis"
          >
            <FaRegSmile />
          </button>

          {isEmojiPickerOpen && (
            <div className="emoji-picker-panel" role="menu" aria-label="Emojis">
              {EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  className="emoji-picker-option"
                  onClick={() => insertEmoji(emoji)}
                  type="button"
                  role="menuitem"
                  aria-label={`Insérer ${emoji}`}
                  disabled={isSending}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={newMessage}
          onChange={(e) => onChangeMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Écrivez un message..."
          className="chat-textarea"
          disabled={isSending}
          rows={1}
        />

        <button
          onClick={onSendMessage}
          className="send-button"
          disabled={isSendDisabled}
          type="button"
          aria-label="Envoyer le message"
        >
          &#10148;
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
